import InlineBanner from '@/components/UI/InlineBanner/InlineBanner';
import { _isNotEmpty } from '@/littledash';
import type { FC } from 'react';
import type { SendCoercionFile } from '@/components/Agents/SendValidator/utils/sendCoercion';

interface SendCoercionWarningsProps {
  /** The files to list, each with at least one warning. */
  files: ReadonlyArray<SendCoercionFile>;
  /** What the user should do about it, which depends on whether validation has run yet. */
  remedy: string;
  testId?: string;
}

const SEND_COERCION_FIX =
  'To fix it, re-export the files from the source system without opening them in Excel, or, if you have to ' +
  'use Excel, import every column as Text and keep dates as YYYY-MM-DD.';

/** What the user should do about it before confirming, said once for the whole block rather than under every column. */
export const SEND_COERCION_REMEDY = `Validation will still run, but some findings may describe changes Excel made rather than your data. ${SEND_COERCION_FIX}`;

/** The same, once the run has gone ahead on the files. */
export const SEND_COERCION_REPORT_REMEDY = `This validation ran on these files as uploaded, so some findings may describe changes Excel made rather than your data. ${SEND_COERCION_FIX} Then validate the new files.`;

/**
 * Said beside the warnings because the patterns' thresholds are our reading, awaiting a SEND practitioner's
 * review: a warning can be wrong, and the user should know that before acting on one.
 */
export const SEND_COERCION_PROVISIONAL =
  'These signs are our current reading and are still under expert review, so a warning can be wrong.';

/**
 * The files whose columns look rewritten by Excel: at the confirm step, before the user commits to a
 * validation run, and in the report, which records that the run went ahead on them.
 *
 * A warning, not a failure: a flat warning banner, unlike the plain list of input failures shown when a
 * run cannot start, and unlike the raised banners conformance findings use. Never counted among the
 * findings. Renders nothing when no file has a warning.
 */
const SendCoercionWarnings: FC<SendCoercionWarningsProps> = ({ files, remedy, testId = 'send-coercion-warnings' }) => {
  if (!_isNotEmpty(files)) {
    return null;
  }

  return (
    <div data-test-component="SendCoercionWarnings" className="mb3">
      <InlineBanner variant="warning" title="These files look like they were saved in Excel" testId={testId}>
        <p className="f6 mt1 mb2" data-test-element="remedy">
          {remedy}
        </p>
        <p className="f6 mt0 mb2 mid-gray" data-test-element="provisional">
          {SEND_COERCION_PROVISIONAL}
        </p>
        <ul className="list pl0 ma0" data-test-element="files">
          {files.map((file) => (
            <li key={file.key} className="mt2" data-test-element="file" data-test-key={file.key}>
              <div className="f6 b" data-test-element="file-name">
                {file.name}
              </div>
              <ul className="list pl0 ma0">
                {file.warnings.map((warning) => (
                  <li
                    key={`${warning.column_name}-${warning.kind}`}
                    className="f6 mt1"
                    data-test-element="warning"
                    data-test-key={`${warning.column_name}-${warning.kind}`}
                  >
                    <span data-test-element="warning-message">{warning.message}</span>{' '}
                    <span className="mid-gray" data-test-element="warning-samples">
                      (e.g. {warning.samples.join(', ')})
                    </span>
                  </li>
                ))}
                {file.omitted > 0 && (
                  <li className="f6 mt1 mid-gray" data-test-element="omitted">
                    …and {file.omitted.toLocaleString('en-US')} more {file.omitted === 1 ? 'warning' : 'warnings'} for
                    this file.
                  </li>
                )}
              </ul>
            </li>
          ))}
        </ul>
      </InlineBanner>
    </div>
  );
};

export default SendCoercionWarnings;
